
import type { StripeCheckout, StripePaymentElement } from '@stripe/stripe-js'
import { defineComponent, PropType } from 'vue'

export default defineComponent({
  name: 'V2CheckoutPaymentCard',
  props: {
    checkout: {
      type: Object as PropType<StripeCheckout | null>,
      default: null
    },
    checkoutActions: {
      type: Object as PropType<any>,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String as PropType<string | null>,
      default: null
    },
    returnUrl: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      paymentElement: null as StripePaymentElement | null,
      paymentReady: false,
      paymentComplete: false,
      paymentProcessing: false,
      paymentError: null as string | null,
      paymentState: 'idle' as 'idle' | 'processing' | 'success' | 'error'
    }
  },
  computed: {
    canSubmit(): boolean {
      return this.paymentReady && this.paymentComplete && !this.paymentProcessing
    }
  },
  watch: {
    checkout: {
      immediate: true,
      handler(newCheckout: StripeCheckout | null) {
        if (newCheckout) {
          this.$nextTick(() => {
            this.initializePaymentElement()
          })
        }
      }
    }
  },
  beforeDestroy() {
    if (this.paymentElement) {
      this.paymentElement.destroy()
      this.paymentElement = null
    }
  },
  methods: {
    initializePaymentElement(): void {
      if (!this.checkout) return

      // Destroy existing payment element if re-initializing (e.g. session re-created after plan change)
      if (this.paymentElement) {
        this.paymentElement.destroy()
        this.paymentElement = null
      }

      this.paymentReady = false
      this.paymentComplete = false
      this.paymentError = null

      // Create and mount Payment Element from the checkout instance provided by parent
      this.paymentElement = this.checkout.createPaymentElement({
        layout: 'tabs'
      })
      this.paymentElement.mount('#v2-payment-element')

      this.paymentElement.on('ready', () => {
        this.paymentReady = true
      })

      this.paymentElement.on('change', (event) => {
        this.paymentComplete = event.complete
      })
    },

    async handlePayment(): Promise<void> {
      if (!this.checkoutActions) return

      this.paymentProcessing = true
      this.paymentState = 'processing'
      this.paymentError = null

      try {
        const result = await this.checkoutActions.confirm({
          returnUrl: this.returnUrl || `${window.location.origin}/`,
          redirect: 'if_required'
        })

        if (result.type === 'error') {
          this.paymentError = result.error.message ?? 'Payment failed. Please try again.'
          this.paymentState = 'error'
          this.$emit('payment-error', this.paymentError)
        } else {
          this.paymentState = 'success'
          this.$emit('payment-success')
        }
      } catch (err) {
        this.paymentState = 'error'
        this.paymentError = (err as Error).message ?? 'An unexpected error occurred.'
        this.$emit('payment-error', this.paymentError)
      } finally {
        this.paymentProcessing = false
      }
    }
  }
})
